// PracticeCardPage.tsx
import {
  Box,
  LuxButton,
  LuxTypography,
  useMediaQuery,
  useTheme,
} from '@gemini/fe-ds-core';
import { GalleryCard } from './GalleryCard';
import { ServicesCard } from './ServicesCard';
import { DoctorsCard } from './DoctorsCard';
import { DirectionsCard } from './DirectionsCard';
import { RatingsCard } from './RatingsCard';
import { LeftButtonsWrapper } from '@components/Steps/Steps.styles';
import { useHistory } from 'react-router-dom';
import { TabletBreakpoint } from '@utils/consts/breakpoints.consts';
import {
  PracticeSectionButtonsWrapper,
  PracticeSectionExternalWrapper,
} from './styles';
import { useUrlsContext } from '@utils/contexts/UrlsContext';
import { useTranslation } from 'react-i18next';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { formatGeneralAddress } from '@components/BookingDetails/utils';
import { APIProvider, useApiIsLoaded } from '@vis.gl/react-google-maps';
import { useEffect, useState } from 'react';
import { ErrorMessage, useEnv } from '@gemini/lux-ds-pms';

export interface GoogleReview {
  reviews: google.maps.places.Review[];
  totalReviewsNumber: number;
}

export function PracticeCardPage() {
  const env = useEnv();
  const { t } = useTranslation();

  const apiKey = env.ONLINEBOOKING_GOOGLE_API_KEY;

  if (!apiKey) {
    console.error(t('practiceCard.errorLoadingGoogleApiKey'));

    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ErrorMessage>
          {t('practiceCard.errorLoadingGoogleApiKey')}
        </ErrorMessage>
      </Box>
    );
  }

  // In order to wrap all with APIProvider and to use useApiIsLoaded inside
  return (
    <APIProvider apiKey={apiKey}>
      <PracticeCardPageContent />
    </APIProvider>
  );
}

function PracticeCardPageContent() {
  const history = useHistory();
  const { t } = useTranslation();
  const theme = useTheme();
  const { Urls } = useUrlsContext();
  const isMobile = useMediaQuery(`(max-width:${TabletBreakpoint}px)`);
  const practiceInfo = useOnlineBookingStore(state => state.generalInfo);
  const brandName = useOnlineBookingStore(state => state.brandName);
  const placeId = useOnlineBookingStore(
    state => state.generalInfo?.googlePlaceId,
  );

  const apiIsLoaded = useApiIsLoaded();
  const [rating, setRating] = useState<number | undefined>(undefined);
  const [reviews, setReviews] = useState<GoogleReview>({
    totalReviewsNumber: 0,
    reviews: [],
  });

  // Effect per ratings/reviews
  useEffect(() => {
    let cancelled = false;

    if (!placeId) {
      setRating(undefined);
      setReviews({
        reviews: [],
        totalReviewsNumber: 0,
      });
      return;
    }
    if (!apiIsLoaded) {
      return;
    }

    (async () => {
      try {
        const { Place } = (await google.maps.importLibrary(
          'places',
        )) as google.maps.PlacesLibrary;

        const place = new Place({ id: placeId });
        await place.fetchFields({
          fields: ['reviews', 'rating', 'userRatingCount'],
        });

        if (cancelled) return;
        setRating(place.rating || undefined);
        setReviews({
          reviews: place.reviews ?? [],
          totalReviewsNumber: place.userRatingCount ?? 0,
        });
      } catch {
        if (!cancelled) {
          setRating(undefined);
          setReviews({
            reviews: [],
            totalReviewsNumber: 0,
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [placeId, apiIsLoaded]);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PracticeSectionExternalWrapper
        backgroundColor={
          brandName && theme.palette.LUX.obTokens.bgColorBaseSecondary
        }
      >
        <GalleryCard rating={rating} reviews={reviews} />
        <ServicesCard />
        <DoctorsCard />
        <DirectionsCard />
        <RatingsCard reviews={reviews} />
      </PracticeSectionExternalWrapper>

      <PracticeSectionButtonsWrapper>
        {!isMobile && (
          <LeftButtonsWrapper>
            <LuxTypography variant="heading2">
              {practiceInfo?.name}
            </LuxTypography>
            <LuxTypography
              variant="textMdRegular"
              color={theme.palette.LUX.obTokens.textColorBaseSecondary}
            >
              {formatGeneralAddress(practiceInfo)}
            </LuxTypography>
          </LeftButtonsWrapper>
        )}
        <LuxButton
          sx={{ width: isMobile ? '100%' : 'unset' }}
          onClick={() => history.push(Urls.APPOINTMENT_TYPE)}
        >
          {t('bookingTitle')}
        </LuxButton>
      </PracticeSectionButtonsWrapper>
    </Box>
  );
}
